import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
  IAPIState,
  IFormButtons,
  IInfoBlockItem,
  IInfoBlockItemType,
  IStatus,
  StateErrorType,
} from "shared/types";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";

import { getInfoBlockItemReadableName } from "shared/utils";
import { ContactForm, FileForm, ImageForm, PlaceForm } from "shared/components";

export interface EditInfoBlockItemBaseProps {
  type: IInfoBlockItem;
  item?: IInfoBlockItemType;
  handleClose: () => void;
}

interface EditInfoBlockItemModalProps extends EditInfoBlockItemBaseProps {
  editItemApiStatus?: IAPIState;
  editItemApiError?: StateErrorType;
  handleSubmit: (editedItem: Partial<IInfoBlockItemType>) => void;
}

export const useEditInfoBlockItem = ({
  type,
  item,
  handleClose,
  handleSubmit,
  editItemApiStatus,
  editItemApiError,
}: EditInfoBlockItemModalProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (editItemApiStatus === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          message: copy.toast.changed,
          status: IStatus.success,
        })
      );

      handleClose();
    }
  }, [editItemApiStatus, dispatch, handleClose]);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "edit_info_block_item_confirm",
      },
      secondary: {
        onClick: handleClose,
        "data-testid": "edit_info_block_item_cancel",
      },
    }),
    [handleClose]
  );

  const formProps = {
    defaultValues: item,
    handleSubmit,
    buttons,
    hasError: !!editItemApiError,
    isSubmitting: editItemApiStatus === IAPIState.pending,
  };

  const Form = () => {
    switch (type) {
      case IInfoBlockItem.contact:
        return ContactForm;
      case IInfoBlockItem.place:
        return PlaceForm;
      case IInfoBlockItem.file:
        return FileForm;
      case IInfoBlockItem.image:
        return ImageForm;
    }
  };

  const itemReadbleName = useMemo(() => getInfoBlockItemReadableName(type), [type]);

  return { itemReadbleName, formProps, Form };
};
